<template>
  <div>
    <div class="justify-content-center">
      <div class="px-0">
        <div class="d-flex i-mb-14 justify-content-between align-items-center">
          <div>
            <settings-section-title id="your-team" class="i-mb-2"
              >Team</settings-section-title
            >
            <p class="i-text-body-2 i-text-neutral-110">
              View and manage how your team is using Inscribe
            </p>
          </div>
          <div id="invite-user-btn">
            <i-button
              :disabled="!hasEditPermissions"
              text="Invite User"
              size="md"
              @button-click="showInviteModal = true"
            >
            </i-button>
          </div>
          <b-tooltip
            v-if="!hasEditPermissions"
            target="invite-user-btn"
            placement="top"
            triggers="hover"
          >
            You don't have permission to invite new users
          </b-tooltip>
        </div>
        <div class="mb-4">
          <b-table
            id="team-table"
            ref="team-table"
            small1
            responsive
            borderless
            :items="usersList"
            :fields="fields"
            class="i-table team-table"
            primary-key="id"
            :per-page="perPage"
            :current-page="currentPage"
          >
            <template #cell(email)="data">
              <div class="d-flex align-items-center">
                <span>{{ data.item.email }}</span>
              </div>
            </template>
            <template #cell(status)="data">
              <div class="i-pill i-text-subheading-3">
                <span
                  v-if="
                    !data.item.confirmed &&
                    data.item.registration_invite_expired
                  "
                  class="error"
                  >Expired</span
                >
                <span v-else-if="!data.item.confirmed" class="warning"
                  >Pending</span
                >
                <span v-else-if="!data.item.is_active" class="neutral">
                  Disabled
                </span>
              </div>
            </template>
            <template #cell(role)="data">
              <div class="d-flex justify-content-between align-items-center">
                <select-input
                  v-if="hasEditPermissions"
                  class="role-dropdown"
                  size="sm"
                  boundary="window"
                  :disabled="isRoleChangeDisabled(data.item)"
                  :loading="loadingIds[data.item.id]"
                  :options="roles"
                  :value="data.item.role"
                  @input="changeUserRole(data.item.id, $event)"
                />
                <span v-else class="text-muted">
                  {{ getRoleName(data.item.role) }}
                </span>
              </div>
            </template>
            <template #cell(actions)="data">
              <div class="d-flex align-items-center">
                <i-button
                  v-if="isActiveStatusChangeAllowed(data.item)"
                  class="change-active-status-button"
                  :text="data.item.is_active ? 'Disable' : 'Enable' + ' user'"
                  data-testid="enable-disable-user-toggle-button"
                  :type="data.item.is_active ? 'error' : 'primary'"
                  size="xs"
                  @button-click="changeUserActiveStatus(data.item)"
                ></i-button>

                <i-button
                  v-if="!data.item.confirmed && hasEditPermissions"
                  :disabled="loadingIds[data.item.id]"
                  text="Resend invite"
                  data-testid="resend-invite-button"
                  subtype="outline"
                  size="xs"
                  @button-click="reinviteUser(data.item.id)"
                ></i-button>

                <i-button
                  v-if="
                    !data.item.confirmed &&
                    !data.item.registration_invite_expired &&
                    hasEditPermissions
                  "
                  :disabled="loadingIds[data.item.id]"
                  text="Cancel invite"
                  data-testid="cancel-invite-button"
                  type="error"
                  subtype="outline"
                  size="xs"
                  class="i-ml-4"
                  @button-click="cancelUserInvite(data.item.id)"
                ></i-button>
              </div>
            </template>
          </b-table>
          <b-pagination
            v-if="showPagination"
            v-model="currentPage"
            :total-rows="usersListLength"
            :per-page="perPage"
            aria-controls="team-table"
            class="i-pagination"
          >
            <template #first-text
              ><span class="chevron">
                <i-icon
                  symbol="medium-chevron-left-double"
                  :size="20"
                ></i-icon></span
            ></template>
            <template #prev-text>
              <span class="chevron">
                <i-icon symbol="medium-chevron-left" :size="20"></i-icon>
              </span>
            </template>
            <template #next-text>
              <span class="chevron">
                <i-icon symbol="medium-chevron-right" :size="20"></i-icon>
              </span>
            </template>
            <template #last-text>
              <span class="chevron">
                <i-icon
                  symbol="medium-chevron-right-double"
                  :size="20"
                ></i-icon>
              </span>
            </template>
          </b-pagination>
        </div>
      </div>
      <b-modal
        id="team-invite-modal"
        ref="team-invite-modal"
        v-model="showInviteModal"
        title="Invite a team member"
        ok-title="Send invite"
        :ok-disabled="!isValidEmail"
        @ok="inviteNewUser"
      >
        <div>
          <p class="mb-4">
            Invite a new user to your Inscribe team with their work email
            address.
          </p>
          <text-input
            :auto-focus="true"
            type="email"
            name="email"
            :value="newEmail"
            label="Email address"
            placeholder="teammate@example.com"
            :error="!isValidEmail"
            validation-message="A valid email is required"
            @input="newEmail = $event.toLowerCase()"
          />
          <b-form-group id="role" label="Role" label-for="new-role-dropdown">
            <select-input
              id="new-role-dropdown"
              v-model="newRole"
              boundary="window"
              class="new-role-dropdown"
              :options="roles"
            />
          </b-form-group>
          <p v-if="isValidEmail" class="mt-4">
            An email will be sent to <em>{{ newEmail }}</em> inviting them to
            create a password to log into this team.
          </p>
        </div>
      </b-modal>
    </div>
    <router-view />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { SelectInput, TextInput } from '@inscribe/ui-kit';
import { iButton, iIcon } from '@inscribe/signal';
import * as userRoles from '@/constants/userRoles';
import { isValidEmail } from '@/utils/validation';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';

export default {
  components: {
    'i-button': iButton,
    'i-icon': iIcon,
    'select-input': SelectInput,
    'text-input': TextInput,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      newEmail: null,
      newRole: userRoles.USER_ROLE,
      loadingIds: {},
      fields: [
        {
          key: 'email',
          label: 'User email',
          sortable: true,
          thClass: 'email-column',
        },
        { key: 'status', label: '', thClass: 'status-column' },
        {
          key: 'role',
          label: 'Role',
          sortable: true,
          thClass: 'role-column',
        },
        { key: 'actions', label: '', thClass: 'actions-column' },
      ],
      roles: [
        {
          value: userRoles.ADMIN_ROLE,
          text: 'Admin',
          subtext:
            'Can view, update, and delete all customers & documents in your workspace, and can invite new users to your team and change their roles.',
        },
        {
          value: userRoles.ADVANCED_USER_ROLE,
          text: 'Advanced User',
          subtext:
            'Can view and update all customers & documents in your workspace',
        },
        {
          value: userRoles.USER_ROLE,
          text: 'User',
          subtext:
            'Can view, update, and delete customers they have created, and documents they have uploaded.',
        },
        {
          value: userRoles.BASIC_USER_ROLE,
          text: 'Basic User',
          subtext:
            'Can view and update customers they have created, and documents they have uploaded.',
        },
      ],
      showInviteModal: false,
      perPage: 10,
      currentPage: 1,
    };
  },
  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
    }),

    ...mapState(['currentUser']),

    isValidEmail() {
      return isValidEmail(this.newEmail);
    },

    usersList() {
      return this.currentUser.organisation.users.filter(
        ({ type }) => type !== 'API',
      );
    },
    usersListLength() {
      return this.usersList.length;
    },

    showPagination() {
      return this.usersList.length > this.perPage;
    },
  },
  mounted() {
    if (this.$route.query?.showInviteModal?.toString() === 'true') {
      this.showInviteModal = true;
    }
  },
  methods: {
    changeUserRole(userId, role) {
      this.$set(this.loadingIds, userId, true);
      this.$store
        .dispatch('changeUserRole', { userId, role })
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('changeUserRole', { userId, role });
            this.$set(this.loadingIds, userId, false);
          }
        })
        .catch((error) => {
          this.$set(this.loadingIds, userId, false);
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'There was a problem changing the user role',
            duration: 5000,
            text: error.response.data.message,
          });
        });
    },
    changeUserActiveStatus(user) {
      const userId = user.id;
      const activeStatus = !user.is_active;
      this.$set(this.loadingIds, userId, true);

      this.$store
        .dispatch('changeUserActiveStatus', { userId, activeStatus })
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('changeUserActiveStatus', {
              userId,
              activeStatus,
            });
          }
        })
        .catch((error) => {
          const statusChange = activeStatus ? 'disabling' : 're-enabling';
          this.$notify({
            group: 'general',
            type: 'error',
            title: `There was a problem ${statusChange} the user`,
            duration: 5000,
            text: error.response.data.message,
          });
        })
        .finally(() => {
          this.$set(this.loadingIds, user.id, false);
        });
    },
    getRoleName(role) {
      return this.roles.find(({ value }) => value === role).text;
    },
    userIsCurrentUser(user) {
      return user.id === this.$store.state.currentUser.uuid;
    },
    isRoleChangeDisabled(user) {
      return (
        !this.hasEditPermissions ||
        this.loadingIds[user.id] ||
        this.userIsCurrentUser(user)
      );
    },
    isActiveStatusChangeAllowed(user) {
      return (
        this.hasEditPermissions &&
        user.confirmed &&
        !this.userIsCurrentUser(user)
      );
    },
    reinviteUser(userId) {
      this.$set(this.loadingIds, userId, true);
      this.$store
        .dispatch('reinviteUser', userId)
        .then(() => {
          this.$set(this.loadingIds, userId, false);
          this.$notify({
            group: 'general',
            type: 'success',
            title: 'Invite resent',
            duration: 2000,
          });
        })
        .catch((error) => {
          this.$set(this.loadingIds, userId, false);
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'There was a problem resending your invite',
            duration: 5000,
            text: error.response.data.message,
          });
        });
    },
    cancelUserInvite(userId) {
      this.$set(this.loadingIds, userId, true);
      this.$store
        .dispatch('cancelUserInvite', userId)
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('cancelUserInvite', {
              userId,
            });
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'There was a problem cancelling the invite',
            duration: 5000,
            text: error.response.data.message,
          });
        })
        .finally(() => {
          this.$set(this.loadingIds, userId, false);
        });
    },
    inviteNewUser() {
      const data = new FormData();
      data.append('email', this.newEmail);
      data.append('role', this.newRole);
      this.$store
        .dispatch('inviteNewUser', data)
        .then((response) => {
          const { uuid: id, email } = response.data.user;
          this.$store.commit('addTeamMember', {
            id,
            email,
            role: this.newRole,
            confirmed: response.data.user.registration_confirmed,
            is_active: response.data.user.is_active,
          });

          this.newRole = userRoles.USER_ROLE;
          this.newEmail = null;
        })
        .catch((error) => {
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'There was a problem sending your invite',
            duration: 5000,
            text: error.response.data.message,
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .team-table {
  &.i-table {
    min-height: 300px;

    tr {
      td {
        .btn.btn-secondary.btn-sm.dropdown-toggle {
          width: 100% !important;
        }
      }
      .change-active-status-button {
        visibility: hidden;
      }
      &:hover {
        .change-active-status-button {
          visibility: visible;
        }
      }
    }

    .email-column {
      width: auto;
    }
    .status-column {
      width: 120px;
    }
    .role-column {
      width: 199px;
    }
    .actions-column {
      width: 240px;
    }
  }
}

::v-deep .role-dropdown,
::v-deep .new-role-dropdown {
  width: 100% !important;
  .select-options-list {
    max-height: 350px;
  }
}
</style>
